import { useState, useEffect, useCallback, useRef, useMemo, type FC } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { createPortal } from "react-dom";
import {
    Combobox,
    ComboboxInput,
    ComboboxButton,
    ComboboxOption,
    ComboboxOptions,
} from "@headlessui/react";
import clsx from "clsx";
import { fetchOrganizations, type OrganizationResponse } from "../../../api/organizations";
import { fetchStores, type StoreResponse } from "../../../api/stores";
import { fetchStoreDepartments, type DepartmentResponse } from "../../../api/departments";
import {
    fetchMyOrganizationLevel,
    LEVEL_FLAG_LABEL,
    LEVEL_FLAG_ORDER,
    LEVEL_FLAG_TO_ORG_LEVEL,
    type OrganizationLevelFlagKey,
    type OrganizationLevelResponse,
} from "../../../api/organizationLevels";
import { ALL_SELECTION_ID } from "../../../app/store/storeSelectionSlice";

// =============================================================================
// 型定義
// =============================================================================

/**
 * アクティブな行の型.
 *
 * organization_level のフラグ ON のものが行として並ぶ。
 * source は行のオプション一覧の出どころで、3 種類:
 *   - "organization": allOrgs を level でフィルタ（level=0..9, 13）
 *   - "store"       : allStores を使う（level=11）
 *   - "department"  : fetchStoreDepartments の結果を使う（level=12）
 */
interface ActiveRow {
    flagKey: OrganizationLevelFlagKey;
    orgLevel: number;
    label: string;
    source: "organization" | "store" | "department";
}

/**
 * 選択結果の型（動的組織階層対応）
 *
 * orgLevels は organization.level をキーとする（0..9, 13）。
 * level=11 (店舗) と level=12 (部門) は store / department フィールドに格納。
 *
 * store.organizationId / department.organizationId は対応する level=11 / level=12 の
 * organization.id を保持する（events API などで organization_id 単一カラムでの
 * スコープ問い合わせに使う）。
 */
export interface StoreSelection {
    orgLevels: Record<number, { id: number; name: string } | null>;
    store: { id: number; organizationId?: number; name: string } | null;
    department: { id: number; organizationId?: number; name: string } | null;
}

/**
 * StoreSwitchPopup の Props
 */
export interface StoreSwitchPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (selection: StoreSelection) => void;
    currentSelection?: StoreSelection;
    anchorRef: React.RefObject<HTMLElement | null>;
    /**
     * 組織レベルのみを選択する簡易モード。
     * 店舗検索 / 店舗 / 部門 セクションを非表示にし、決定可否は組織レベルだけで判定する。
     */
    orgOnly?: boolean;
    /**
     * 表示する組織階層の最大 depth（root=0 を除く 1 始まり）。
     * 例: maxDepth=1 → 第1階層のみ表示。未指定なら全階層を表示。
     */
    maxDepth?: number;
    /**
     * organization.level がこの値未満の組織だけを popup に表示する。
     * 例: maxLevel=11 → level<11 の組織だけ（店舗・部門レベルを除外）。
     * 未指定なら level による絞り込みなし。
     */
    maxLevel?: number;
    /**
     * 第1階層（root レベル）の選択を非活性にする。
     * 法人を変えさせたくない（他社の店舗にできちゃう）ようなフォームで使う。
     */
    lockFirstLevel?: boolean;
    /**
     * ポップアップの配置方針。
     * - "below" (default): アンカーの真下、収まらなければ上 → 右の順でフォールバック
     * - "right": アンカーの右横（縦はアンカー中央揃え）。サイドメニュー脇に沿わせる用途
     */
    placement?: "below" | "right";
}

// --- モジュールレベルキャッシュ（セッション中の再取得を防止） ---
const CACHE_TTL = 5 * 60 * 1000; // 5分
interface PopupDataCache {
    organizationLevel: OrganizationLevelResponse;
    organizations: OrganizationResponse[];
    stores: StoreResponse[];
    activeRows: ActiveRow[];
    timestamp: number;
}
let dataCache: PopupDataCache | null = null;

const ALL_STORE: StoreResponse = {
    id: ALL_SELECTION_ID, organization_id: null, name: "全店舗",
    code: null, is_active: true, created_at: null, updated_at: null,
};
const ALL_DEPARTMENT: DepartmentResponse = {
    id: ALL_SELECTION_ID, organization_id: 0, code: "", name: "全部門",
};

const isAll = (item: { id: number } | null): boolean => item?.id === ALL_SELECTION_ID;

/** 「全〇〇」センチネルを動的に生成（上位階層 / 課用） */
function makeAllOrgSentinel(orgLevel: number, label: string): OrganizationResponse {
    return {
        id: ALL_SELECTION_ID, tenant_id: "", parent_organization_id: null,
        name: `全${label}`, short_name: null, description: null,
        level: orgLevel,
    };
}

/**
 * organization_level のフラグから ActiveRow[] を構築する.
 *
 * 描画順は LEVEL_FLAG_ORDER（first..tenth → store → department → section）。
 * フラグ=true のものだけを返す。
 *
 * NOTE: level=0 (first) の行は「テナントルート 1 件のみ」なので呼び出し側で
 *       自動選択して非表示にすることを推奨。本関数では一律に行として返す。
 */
export function buildActiveRows(level: OrganizationLevelResponse): ActiveRow[] {
    const rows: ActiveRow[] = [];
    for (const flagKey of LEVEL_FLAG_ORDER) {
        if (!level[flagKey]) continue;
        const orgLevel = LEVEL_FLAG_TO_ORG_LEVEL[flagKey];
        const label = LEVEL_FLAG_LABEL[flagKey];
        const source: ActiveRow["source"] =
            flagKey === "store" ? "store"
            : flagKey === "department" ? "department"
            : "organization";
        rows.push({ flagKey, orgLevel, label, source });
    }
    return rows;
}

/**
 * ある orgLevel の親に当たる行を、ActiveRow リストの中から探す.
 *
 * organization.parent_organization_id チェーンは、organization_level で
 * 「使用する」とマークされたレベル群の中で連鎖していると仮定する。
 * 例: first=1, second=0, store=1 のとき、店舗(level=11)の親は first(level=0)。
 *
 * @returns 自分より小さい orgLevel をもつ最大の ActiveRow（無ければ null）
 */
function findParentRow(orgLevel: number, rows: ActiveRow[]): ActiveRow | null {
    let best: ActiveRow | null = null;
    for (const r of rows) {
        if (r.orgLevel < orgLevel && (best === null || r.orgLevel > best.orgLevel)) {
            best = r;
        }
    }
    return best;
}

/**
 * 権限閾値: 行ごとの permissionLevel 必要値.
 *
 * - 上位階層 (level=0..9): 9999（管理者専用、ただし level=0 は自動選択で UI には出ない）
 * - 店舗 (level=11)    : 50
 * - 部門 (level=12)    : 40
 * - 課   (level=13)    : 30
 */
function getPermissionThreshold(orgLevel: number): number {
    if (orgLevel === 11) return 50;
    if (orgLevel === 12) return 40;
    if (orgLevel === 13) return 30;
    return 9999;
}

// =============================================================================
// 汎用 Combobox 行コンポーネント
// =============================================================================

interface ComboboxRowProps<T> {
    label: string;
    value: T | null;
    onChange: (value: T | null) => void;
    options: T[];
    query: string;
    onQueryChange: (q: string) => void;
    placeholder?: string;
    loading?: boolean;
    disabled?: boolean;
}

function ComboboxRow<T extends { id: number; name: string | null }>({
    label,
    value,
    onChange,
    options,
    query,
    onQueryChange,
    placeholder = "検索...",
    loading = false,
    disabled = false,
}: ComboboxRowProps<T>) {
    return (
        <div className={clsx("flex items-center gap-3", disabled && "opacity-50")}>
            <label className="text-xs text-gray-500 w-24 shrink-0 text-right">
                {label}
            </label>
            <Combobox
                value={value}
                onChange={onChange}
                onClose={() => onQueryChange("")}
                disabled={disabled}
                immediate
            >
                <div className="relative flex-1">
                    <ComboboxInput
                        className={clsx(
                            "w-full rounded-md border px-3 py-2 pr-8 text-sm",
                            "transition-colors duration-200",
                            disabled
                                ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                                : "bg-white border-gray-300 hover:border-gray-400 text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 cursor-pointer",
                        )}
                        displayValue={(item: T | null) => item?.name ?? ""}
                        onChange={(e) => onQueryChange(e.target.value)}
                        placeholder={placeholder}
                        readOnly
                        inputMode="none"
                    />
                    <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <svg className={clsx("w-4 h-4", disabled ? "text-gray-300" : "text-gray-400")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </ComboboxButton>
                </div>
                <ComboboxOptions
                    anchor="bottom start"
                    className="z-[10000] w-[var(--input-width)] max-h-40 overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg"
                >
                    {options.length === 0 ? (
                        <div className="px-3 py-2 text-gray-400 text-xs">
                            {loading ? "読み込み中..." : "候補がありません"}
                        </div>
                    ) : (
                        options.map((item) => (
                            <ComboboxOption
                                key={item.id}
                                value={item}
                                className="cursor-pointer select-none px-3 py-1.5 data-[focus]:bg-brand-50 data-[focus]:text-brand-700"
                            >
                                {item.name}
                            </ComboboxOption>
                        ))
                    )}
                </ComboboxOptions>
            </Combobox>
        </div>
    );
}

// =============================================================================
// メインコンポーネント
// =============================================================================

/**
 * StoreSwitchPopup - 店舗切替ポップアップ
 *
 * `organization_level` テーブルの使用フラグに基づき、行を動的にレンダリングする。
 * - first..tenth フラグ=true → 上位組織階層（organization.level=0..9）
 * - store      フラグ=true → 店舗 (organization.level=11)
 * - department フラグ=true → 部門 (organization.level=12)
 * - section    フラグ=true → 課   (organization.level=13)
 */
export const StoreSwitchPopup: FC<StoreSwitchPopupProps> = ({
    isOpen,
    onClose,
    onSelect,
    currentSelection,
    anchorRef,
    // TODO: feature ブランチで activeRows ベースに作り直された影響で、以下 4 props は
    // 一旦受け取るだけで未使用。store form 等の挙動を維持したい場合は再実装が必要。
    orgOnly: _orgOnly = false,
    maxDepth: _maxDepth,
    maxLevel: _maxLevel,
    lockFirstLevel: _lockFirstLevel = false,
    placement = "below",
}) => {
    const { user } = useAuth();
    const permissionLevel = user?.permissionLevel ?? 0;

    const popupRef = useRef<HTMLDivElement>(null);
    const initialDeptRestoredRef = useRef(false);
    const previousDepartmentIdRef = useRef<number | null>(null);
    const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);

    // --- 全データ ---
    const [orgLevelConfig, setOrgLevelConfig] = useState<OrganizationLevelResponse | null>(null);
    const [allOrgs, setAllOrgs] = useState<OrganizationResponse[]>([]);
    const [allStores, setAllStores] = useState<StoreResponse[]>([]);
    const [departments, setDepartments] = useState<DepartmentResponse[]>([]);
    const [activeRows, setActiveRows] = useState<ActiveRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [deptLoading, setDeptLoading] = useState(false);

    // --- 選択値 ---
    // key = organization.level (0..9, 13)。level=11/12 は selectedStore/selectedDepartment を使う
    const [selectedOrgLevels, setSelectedOrgLevels] = useState<Record<number, OrganizationResponse | null>>({});
    const [selectedStore, setSelectedStore] = useState<StoreResponse | null>(null);
    const [selectedDepartment, setSelectedDepartment] = useState<DepartmentResponse | null>(null);

    // --- 検索クエリ ---
    const [orgQueries, setOrgQueries] = useState<Record<number, string>>({});
    const [storeQuery, setStoreQuery] = useState("");
    const [deptQuery, setDeptQuery] = useState("");
    const [globalStoreQuery, setGlobalStoreQuery] = useState("");

    // --- ヘルパー ---
    const findOrg = useCallback(
        (id: number): OrganizationResponse | null => allOrgs.find(a => a.id === id) ?? null,
        [allOrgs],
    );

    /**
     * ある組織の親を辿って、各 organization.level に対する祖先を集める.
     * 戻り値の key は organization.level。
     */
    const resolveAncestorsByLevel = useCallback(
        (orgId: number): Record<number, OrganizationResponse | null> => {
            const result: Record<number, OrganizationResponse | null> = {};
            let current = findOrg(orgId);
            while (current) {
                result[current.level] = current;
                current = current.parent_organization_id ? findOrg(current.parent_organization_id) : null;
            }
            return result;
        },
        [findOrg],
    );

    // --- アンカー位置計算 ---
    useEffect(() => {
        if (!isOpen) {
            // 閉じたら位置をクリアして、次回オープン時に左上へ一瞬出るのを防ぐ
            setPosition(null);
            return;
        }
        if (loading) {
            // データ取得中は popup が DOM に居ない (return null される) ので
            // popupRef.current が null。fallback dim で測ると alignRight 判定が
            // 狂うため、ここでは何もせず loading 完了の再実行を待つ。
            setPosition(null);
            return;
        }
        if (!anchorRef.current) return;

        const updatePosition = () => {
            const rect = anchorRef.current?.getBoundingClientRect();
            if (!rect) return;

            const popupEl = popupRef.current;
            const pw = popupEl?.offsetWidth ?? 420;
            const ph = popupEl?.offsetHeight ?? 300;
            const gap = 4;
            const pad = 8;

            let top: number;
            let left: number;

            if (placement === "right") {
                // アンカー右横に、縦はアンカー中央揃え（サイドメニュー脇に沿わせる用途）
                const anchorCenterY = rect.top + rect.height / 2;
                top = anchorCenterY - ph / 2;
                left = rect.right + gap;

                // 縦方向: 画面外に出ないようクランプ
                top = Math.max(pad, Math.min(top, window.innerHeight - ph - pad));

                // 横方向: 画面右端を超える場合のみ左にシフト
                if (left + pw > window.innerWidth - pad) {
                    left = Math.max(pad, window.innerWidth - pw - pad);
                }
            } else {
                // 既定: 下 → 上 → 右 の優先で配置（PageHeader の右上ボタン等で使う）
                const fitsBelow = rect.bottom + gap + ph <= window.innerHeight - pad;
                const fitsAbove = rect.top - gap - ph >= pad;

                if (fitsBelow) {
                    top = rect.bottom + gap;
                    left = rect.left;
                } else if (fitsAbove) {
                    top = rect.top - ph - gap;
                    left = rect.left;
                } else {
                    top = Math.max(pad, Math.min(rect.top, window.innerHeight - ph - pad));
                    left = rect.right + gap;
                }

                // popup が画面右端を超えるなら、画面内に収めるよう左にシフト
                if (left + pw > window.innerWidth - pad) {
                    left = Math.max(pad, window.innerWidth - pw - pad);
                }
            }

            setPosition({ top, left, width: rect.width });
        };

        const rafId = requestAnimationFrame(updatePosition);
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
        };
    }, [isOpen, anchorRef, loading, allOrgs.length, placement]);

    // --- 全データ取得 ---
    useEffect(() => {
        if (!isOpen) return;

        initialDeptRestoredRef.current = false;
        setSelectedOrgLevels({});
        setSelectedStore(null);
        setSelectedDepartment(null);
        setDepartments([]);
        setOrgQueries({});
        setStoreQuery("");
        setDeptQuery("");
        setGlobalStoreQuery("");

        if (dataCache && Date.now() - dataCache.timestamp < CACHE_TTL) {
            setOrgLevelConfig(dataCache.organizationLevel);
            setAllOrgs(dataCache.organizations);
            setAllStores(dataCache.stores);
            setActiveRows(dataCache.activeRows);
            return;
        }

        setLoading(true);
        Promise.all([
            fetchMyOrganizationLevel(),
            fetchOrganizations(),
            fetchStores(),
        ])
            .then(([levelRes, orgsRes, storesRes]) => {
                const rows = buildActiveRows(levelRes);
                dataCache = {
                    organizationLevel: levelRes,
                    organizations: orgsRes.items,
                    stores: storesRes.items,
                    activeRows: rows,
                    timestamp: Date.now(),
                };
                setOrgLevelConfig(levelRes);
                setAllOrgs(orgsRes.items);
                setAllStores(storesRes.items);
                setActiveRows(rows);
            })
            .catch((error) => {
                console.error("StoreSwitchPopup データ取得失敗:", error);
            })
            .finally(() => setLoading(false));
    }, [isOpen]);

    // --- level=0 (テナントルート) の自動選択 ---
    // first フラグ=true かつ level=0 の組織が 1 件のみのとき、UI には出さず自動セット
    useEffect(() => {
        if (!orgLevelConfig?.first) return;
        const rootOrgs = allOrgs.filter(o => o.level === 0);
        if (rootOrgs.length !== 1) return;
        const root = rootOrgs[0];
        setSelectedOrgLevels(prev => prev[0] ? prev : { ...prev, 0: root });
    }, [orgLevelConfig, allOrgs]);

    // --- 初期値の反映 ---
    useEffect(() => {
        if (!currentSelection || allOrgs.length === 0 || allStores.length === 0 || activeRows.length === 0) return;

        const restored: Record<number, OrganizationResponse | null> = {};
        for (const row of activeRows) {
            if (row.source !== "organization") continue;
            const sel = currentSelection.orgLevels[row.orgLevel];
            if (!sel) continue;
            if (sel.id === ALL_SELECTION_ID) {
                restored[row.orgLevel] = makeAllOrgSentinel(row.orgLevel, row.label);
            } else {
                restored[row.orgLevel] = findOrg(sel.id);
            }
        }

        // 復元された store / department から上位階層を補完（古い localStorage の救済）
        if (currentSelection.store && currentSelection.store.id !== ALL_SELECTION_ID) {
            const store = allStores.find(s => s.id === currentSelection.store?.id);
            if (store?.organization_id) {
                const ancestors = resolveAncestorsByLevel(store.organization_id);
                for (const [lvlStr, org] of Object.entries(ancestors)) {
                    const lvl = Number(lvlStr);
                    if (lvl <= 9 && !restored[lvl] && org) {
                        restored[lvl] = org;
                    }
                }
            }
        }
        if (currentSelection.department?.organizationId) {
            const ancestors = resolveAncestorsByLevel(currentSelection.department.organizationId);
            for (const [lvlStr, org] of Object.entries(ancestors)) {
                const lvl = Number(lvlStr);
                if (lvl <= 9 && !restored[lvl] && org) {
                    restored[lvl] = org;
                }
            }
        }

        setSelectedOrgLevels(prev => ({ ...prev, ...restored }));

        if (currentSelection.store) {
            if (currentSelection.store.id === ALL_SELECTION_ID) {
                setSelectedStore(ALL_STORE);
            } else {
                const store = allStores.find(s => s.id === currentSelection.store?.id);
                if (store) setSelectedStore(store);
            }
        }
    }, [isOpen, currentSelection, allOrgs, allStores, activeRows, findOrg, resolveAncestorsByLevel]);

    // --- 部門取得 ---
    // isOpen を依存に含めるのは、確定 → 再オープン時に
    // データ effect の reset と「初期値の反映」effect の restore が
    // 同一コミット内で打ち消し合って selectedStore の参照が変わらず、
    // この effect が再実行されないバグを避けるため。
    useEffect(() => {
        if (!isOpen) return;
        if (!selectedStore) {
            setDepartments([]);
            return;
        }

        if (isAll(selectedStore)) {
            const storesToFetch = allStores;
            if (storesToFetch.length === 0) {
                setDepartments([]);
                initialDeptRestoredRef.current = true;
                return;
            }
            setDeptLoading(true);
            Promise.all(storesToFetch.map(s => fetchStoreDepartments(s.id).catch(() => ({ items: [] as DepartmentResponse[] }))))
                .then((results) => {
                    const seen = new Set<number>();
                    const merged: DepartmentResponse[] = [];
                    for (const res of results) {
                        for (const d of res.items) {
                            if (!seen.has(d.id)) {
                                seen.add(d.id);
                                merged.push(d);
                            }
                        }
                    }
                    setDepartments(merged);
                    if (!selectedDepartment || isAll(selectedDepartment)) {
                        setSelectedDepartment(ALL_DEPARTMENT);
                    }
                    initialDeptRestoredRef.current = true;
                })
                .finally(() => setDeptLoading(false));
            return;
        }

        setDeptLoading(true);
        fetchStoreDepartments(selectedStore.id)
            .then((res) => {
                setDepartments(res.items);
                let restoredDept: DepartmentResponse | null = null;
                if (currentSelection?.department && !initialDeptRestoredRef.current) {
                    const targetId = currentSelection.department.id;
                    if (targetId === ALL_SELECTION_ID) {
                        setSelectedDepartment(ALL_DEPARTMENT);
                    } else {
                        restoredDept = res.items.find(d => d.id === targetId) ?? res.items[0] ?? null;
                        setSelectedDepartment(restoredDept);
                    }
                } else {
                    const prevId = previousDepartmentIdRef.current;
                    restoredDept = prevId != null ? res.items.find(d => d.id === prevId) ?? null : null;
                    restoredDept = restoredDept ?? res.items[0] ?? null;
                    setSelectedDepartment(restoredDept);
                }
                if (restoredDept && !isAll(restoredDept) && restoredDept.organization_id) {
                    const ancestors = resolveAncestorsByLevel(restoredDept.organization_id);
                    if (Object.keys(ancestors).length > 0) {
                        const filtered: Record<number, OrganizationResponse | null> = {};
                        for (const [lvlStr, org] of Object.entries(ancestors)) {
                            const lvl = Number(lvlStr);
                            if (lvl <= 9 && org) filtered[lvl] = org;
                        }
                        setSelectedOrgLevels(prev => ({ ...prev, ...filtered }));
                    }
                }
                initialDeptRestoredRef.current = true;
            })
            .catch(() => {
                setDepartments([]);
                initialDeptRestoredRef.current = true;
            })
            .finally(() => setDeptLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, selectedStore, currentSelection]);

    // --- グローバル店舗検索 ---
    const globalFilteredStores = useMemo(() => {
        if (!globalStoreQuery) return [];
        return allStores.filter(s => s.name.includes(globalStoreQuery));
    }, [allStores, globalStoreQuery]);

    // --- 上位階層 / 課行のフィルタ済みオプション ---
    const filteredOrgOptions = useMemo(() => {
        const result: Record<number, OrganizationResponse[]> = {};
        for (const row of activeRows) {
            if (row.source !== "organization") continue;
            let items = allOrgs.filter(o => o.level === row.orgLevel);

            const parent = findParentRow(row.orgLevel, activeRows);
            if (parent) {
                let parentSelection: { id: number } | null;
                if (parent.source === "organization") {
                    parentSelection = selectedOrgLevels[parent.orgLevel] ?? null;
                } else if (parent.source === "store") {
                    parentSelection = selectedStore && !isAll(selectedStore) && selectedStore.organization_id
                        ? { id: selectedStore.organization_id }
                        : null;
                } else {
                    parentSelection = selectedDepartment && !isAll(selectedDepartment) && selectedDepartment.organization_id
                        ? { id: selectedDepartment.organization_id }
                        : null;
                }
                if (parentSelection && !isAll(parentSelection)) {
                    items = items.filter(o => o.parent_organization_id === parentSelection.id);
                }
            }

            const query = orgQueries[row.orgLevel] ?? "";
            if (query) items = items.filter(o => (o.name ?? "").includes(query));

            result[row.orgLevel] = [makeAllOrgSentinel(row.orgLevel, row.label), ...items];
        }
        return result;
    }, [allOrgs, activeRows, selectedOrgLevels, selectedStore, selectedDepartment, orgQueries]);

    // --- フィルタ済み店舗（上位階層の最下位の選択で絞り込み） ---
    // store.organization_id は level=11 の組織 id を指す。
    // 上位階層 (level=0..9) の選択 X が決まったら、parent_organization_id=X の level=11 組織の集合
    // を作り、その organization_id を持つ店舗のみに絞る。
    const filteredStores = useMemo(() => {
        let items = allStores;
        const parentRow = findParentRow(11, activeRows);
        if (parentRow && parentRow.source === "organization") {
            const parentSel = selectedOrgLevels[parentRow.orgLevel];
            if (parentSel && !isAll(parentSel)) {
                const matchingLevel11Ids = new Set(
                    allOrgs
                        .filter(o => o.level === 11 && o.parent_organization_id === parentSel.id)
                        .map(o => o.id),
                );
                items = allStores.filter(s => s.organization_id !== null && matchingLevel11Ids.has(s.organization_id));
            }
        }
        if (storeQuery) items = items.filter(s => s.name.includes(storeQuery));
        return [ALL_STORE, ...items];
    }, [allStores, allOrgs, activeRows, selectedOrgLevels, storeQuery]);

    // --- フィルタ済み部門 ---
    const filteredDepartments = useMemo(() => {
        let items = departments;
        if (deptQuery) items = items.filter(d => d.name.includes(deptQuery));
        return [ALL_DEPARTMENT, ...items];
    }, [departments, deptQuery]);

    // --- 上位階層 / 課の選択ハンドラ ---
    const handleOrgLevelChange = useCallback((orgLevel: number, value: OrganizationResponse | null) => {
        if (isAll(value)) {
            // 「全〇〇」を選んだら、自分以下のすべての行をリセット
            const updates: Record<number, OrganizationResponse | null> = {
                [orgLevel]: makeAllOrgSentinel(orgLevel, LEVEL_FLAG_LABEL[
                    LEVEL_FLAG_ORDER.find(k => LEVEL_FLAG_TO_ORG_LEVEL[k] === orgLevel) ?? "first"
                ]),
            };
            for (const row of activeRows) {
                if (row.source === "organization" && row.orgLevel > orgLevel) {
                    updates[row.orgLevel] = makeAllOrgSentinel(row.orgLevel, row.label);
                }
            }
            setSelectedOrgLevels(prev => ({ ...prev, ...updates }));
            setSelectedStore(ALL_STORE);
            setSelectedDepartment(ALL_DEPARTMENT);
        } else if (value) {
            const ancestors = resolveAncestorsByLevel(value.id);
            const updates: Record<number, OrganizationResponse | null> = {};
            // 自分自身
            updates[orgLevel] = value;
            // 自分より上位の祖先で、かつ activeRows に含まれているもの
            for (const row of activeRows) {
                if (row.source !== "organization") continue;
                if (row.orgLevel < orgLevel) {
                    updates[row.orgLevel] = ancestors[row.orgLevel] ?? null;
                } else if (row.orgLevel > orgLevel) {
                    // 子側はリセット
                    updates[row.orgLevel] = null;
                }
            }
            setSelectedOrgLevels(prev => ({ ...prev, ...updates }));
            setSelectedStore(null);
            setSelectedDepartment(null);
            setDepartments([]);
        } else {
            setSelectedOrgLevels(prev => ({ ...prev, [orgLevel]: null }));
        }
    }, [activeRows, resolveAncestorsByLevel]);

    // --- 店舗選択ハンドラ ---
    const handleStoreChange = useCallback((value: StoreResponse | null) => {
        previousDepartmentIdRef.current = selectedDepartment?.id ?? null;
        setSelectedStore(value);
        if (isAll(value)) {
            setSelectedDepartment(ALL_DEPARTMENT);
        } else {
            setSelectedDepartment(null);
            setDepartments([]);
            // ボトムアップ: 店舗の所属組織から上位を自動解決
            if (value?.organization_id) {
                const ancestors = resolveAncestorsByLevel(value.organization_id);
                const filtered: Record<number, OrganizationResponse | null> = {};
                for (const [lvlStr, org] of Object.entries(ancestors)) {
                    const lvl = Number(lvlStr);
                    if (lvl <= 9 && org) filtered[lvl] = org;
                }
                setSelectedOrgLevels(prev => ({ ...prev, ...filtered }));
            }
        }
    }, [selectedDepartment, resolveAncestorsByLevel]);

    // --- 部門選択ハンドラ ---
    const handleDepartmentChange = useCallback((value: DepartmentResponse | null) => {
        setSelectedDepartment(value);
        if (value && !isAll(value) && value.organization_id) {
            const ancestors = resolveAncestorsByLevel(value.organization_id);
            const filtered: Record<number, OrganizationResponse | null> = {};
            for (const [lvlStr, org] of Object.entries(ancestors)) {
                const lvl = Number(lvlStr);
                if (lvl <= 9 && org) filtered[lvl] = org;
            }
            if (Object.keys(filtered).length > 0) {
                setSelectedOrgLevels(prev => ({ ...prev, ...filtered }));
            }
        }
    }, [resolveAncestorsByLevel]);

    // --- グローバル検索から店舗選択 ---
    const handleGlobalStoreSelect = useCallback((value: StoreResponse | null) => {
        if (!value) return;
        setGlobalStoreQuery("");
        handleStoreChange(value);
    }, [handleStoreChange]);

    // --- 描画対象の行（level=0 自動選択分は除外） ---
    const visibleRows = useMemo(() => {
        return activeRows.filter(row => {
            if (row.source !== "organization") return true;
            // level=0 (first) は 1 件のみのテナントルートで自動選択するので非表示
            if (row.orgLevel === 0) return false;
            return true;
        });
    }, [activeRows]);

    // --- 決定ボタンの有効条件 ---
    const isComplete = useMemo(() => {
        if (activeRows.length === 0) return false;
        for (const row of activeRows) {
            if (row.source === "organization") {
                if (selectedOrgLevels[row.orgLevel] == null) return false;
            } else if (row.source === "store") {
                if (selectedStore == null) return false;
            } else if (row.source === "department") {
                if (selectedDepartment == null) return false;
            }
        }
        return true;
    }, [activeRows, selectedOrgLevels, selectedStore, selectedDepartment]);

    const handleConfirm = useCallback(() => {
        if (!isComplete) return;

        const orgLevels: Record<number, { id: number; name: string } | null> = {};
        for (const row of activeRows) {
            if (row.source !== "organization") continue;
            const sel = selectedOrgLevels[row.orgLevel];
            orgLevels[row.orgLevel] = sel ? { id: sel.id, name: sel.name ?? "" } : null;
        }

        const storeOut = selectedStore
            ? {
                id: selectedStore.id,
                organizationId: isAll(selectedStore) || selectedStore.organization_id == null
                    ? undefined
                    : selectedStore.organization_id,
                name: selectedStore.name,
            }
            : null;
        const departmentOut = selectedDepartment
            ? {
                id: selectedDepartment.id,
                organizationId: isAll(selectedDepartment) || isAll(selectedStore)
                    ? undefined
                    : selectedDepartment.organization_id,
                name: selectedDepartment.name,
            }
            : null;

        onSelect({ orgLevels, store: storeOut, department: departmentOut });
        onClose();
    }, [isComplete, activeRows, selectedOrgLevels, selectedStore, selectedDepartment, onSelect, onClose]);

    // --- 外側クリックで閉じる ---
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest?.("[role='listbox']")) return;

            if (
                popupRef.current && !popupRef.current.contains(target) &&
                anchorRef.current && !anchorRef.current.contains(target)
            ) {
                onClose();
            }
        };

        const timer = setTimeout(() => {
            document.addEventListener("mousedown", handleClickOutside);
        }, 0);

        return () => {
            clearTimeout(timer);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose, anchorRef]);

    // 描画ガード
    if (!isOpen || loading) return null;
    if (currentSelection?.department && !selectedDepartment && !initialDeptRestoredRef.current) return null;

    // 位置計算前は不可視で描画する（実サイズを測るために DOM は必要、左上に出るのは防ぐ）
    const positioned = position !== null;

    // 「店舗」行が活性かどうか（global 店舗検索の disabled も決める）
    const storeRowEnabled = activeRows.some(r => r.source === "store");
    const storeRowDisabled = !storeRowEnabled || permissionLevel < getPermissionThreshold(11);

    return createPortal(
        <div
            ref={popupRef}
            className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[320px]"
            style={{
                top: position?.top ?? 0,
                left: position?.left ?? 0,
                width: position ? Math.max(position.width, 320) : undefined,
                visibility: positioned ? "visible" : "hidden",
                pointerEvents: positioned ? "auto" : "none",
            }}
        >
            {/* グローバル店舗検索（store 行が有効なときだけ） */}
            {storeRowEnabled && (
                <div className={clsx("mb-3 pb-3 border-b border-gray-200", storeRowDisabled && "opacity-50")}>
                    <Combobox
                        value={null as StoreResponse | null}
                        onChange={handleGlobalStoreSelect}
                        onClose={() => setGlobalStoreQuery("")}
                        disabled={storeRowDisabled}
                        immediate
                    >
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                <svg className={clsx("w-4 h-4", storeRowDisabled ? "text-gray-300" : "text-gray-400")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <ComboboxInput
                                className={clsx(
                                    "w-full rounded-md border pl-9 pr-3 py-2 text-sm",
                                    "transition-colors duration-200",
                                    storeRowDisabled
                                        ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                                        : "bg-white border-gray-300 hover:border-gray-400 text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500",
                                )}
                                displayValue={() => globalStoreQuery}
                                onChange={(e) => setGlobalStoreQuery(e.target.value)}
                                placeholder="店舗名で検索..."
                                readOnly={storeRowDisabled}
                            />
                        </div>
                        {globalStoreQuery && (
                            <ComboboxOptions
                                anchor="bottom start"
                                className="z-[10000] w-[var(--input-width)] max-h-48 overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg"
                            >
                                {globalFilteredStores.length === 0 ? (
                                    <div className="px-3 py-2 text-gray-400 text-xs">候補がありません</div>
                                ) : (
                                    globalFilteredStores.map((store) => (
                                        <ComboboxOption
                                            key={store.id}
                                            value={store}
                                            className="cursor-pointer select-none px-3 py-1.5 data-[focus]:bg-brand-50 data-[focus]:text-brand-700"
                                        >
                                            {store.name}
                                        </ComboboxOption>
                                    ))
                                )}
                            </ComboboxOptions>
                        )}
                    </Combobox>
                </div>
            )}

            {/* 動的階層選択 */}
            <div className="space-y-3">
                {visibleRows.map((row) => {
                    const disabled = permissionLevel < getPermissionThreshold(row.orgLevel);

                    if (row.source === "store") {
                        return (
                            <ComboboxRow
                                key={`row-${row.flagKey}`}
                                label={row.label}
                                value={selectedStore}
                                onChange={handleStoreChange}
                                options={filteredStores}
                                query={storeQuery}
                                onQueryChange={setStoreQuery}
                                loading={loading}
                                disabled={disabled}
                            />
                        );
                    }
                    if (row.source === "department") {
                        return (
                            <ComboboxRow
                                key={`row-${row.flagKey}`}
                                label={row.label}
                                value={selectedDepartment}
                                onChange={handleDepartmentChange}
                                options={filteredDepartments}
                                query={deptQuery}
                                onQueryChange={setDeptQuery}
                                loading={deptLoading}
                                disabled={disabled}
                            />
                        );
                    }
                    return (
                        <ComboboxRow
                            key={`row-${row.flagKey}`}
                            label={row.label}
                            value={selectedOrgLevels[row.orgLevel] ?? null}
                            onChange={(v) => handleOrgLevelChange(row.orgLevel, v as OrganizationResponse | null)}
                            options={filteredOrgOptions[row.orgLevel] ?? []}
                            query={orgQueries[row.orgLevel] ?? ""}
                            onQueryChange={(q) => setOrgQueries(prev => ({ ...prev, [row.orgLevel]: q }))}
                            loading={loading}
                            disabled={disabled}
                        />
                    );
                })}
            </div>

            <div className="mt-4 flex justify-end">
                <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={!isComplete}
                    className={clsx(
                        "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                        isComplete
                            ? "bg-brand-500 text-white hover:bg-brand-600"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed",
                    )}
                >
                    決定
                </button>
            </div>
        </div>,
        document.body,
    );
};

export default StoreSwitchPopup;
